import {Alert, Button, Typography} from '@mui/material';
import {useNavigate} from 'react-router-dom';
import {useLicenseQuota} from 'src/hooks/useLicenseQuota';
import {usePdkAutoSyncStatus} from 'src/hooks/usePdkResidentLock';

/** Portal-wide notice when unit licenses cannot cover new rooms or PDK people. */
export default function LicenseInsufficientBanner() {
  const navigate = useNavigate();
  const {isAtLimit, isInsufficient, usedUnits, totalUnits} = useLicenseQuota();
  const {locked, licenseBlocked} = usePdkAutoSyncStatus();

  if (!isInsufficient && !licenseBlocked) return null;

  let title = 'Not enough licenses';
  let body: string;

  if (isAtLimit) {
    title = 'License limit exceeded';
    body = `You are using ${usedUnits} units but only have ${totalUnits} licensed. Most features have been disabled. Please add more licenses to continue.`;
  } else if (isInsufficient) {
    body =
      totalUnits > 0
        ? `You are using ${usedUnits} of ${totalUnits} licensed units. Add licenses before creating more units.`
        : 'This organization has no active unit licenses. Add licenses to create units and residents.';
  } else {
    body =
      'A PDK person was not created in Entrio because licenses were insufficient. Add licenses if needed, then update that person in pdk.io to retry.';
  }

  if (locked && isInsufficient) {
    body +=
      ' New people added in pdk.io will not be created in Entrio until you have a free unit license. Existing synced residents still update. That person stays in PDK.';
  }

  return (
    <Alert
      severity="error"
      sx={{mb: 2}}
      action={
        <Button
          color="error"
          size="small"
          variant="contained"
          onClick={() => navigate('/settings/license')}
        >
          Manage Licenses
        </Button>
      }
    >
      <Typography variant="body1" sx={{fontWeight: 600, mb: 0.5}}>
        {title}
      </Typography>
      <Typography variant="body2">{body}</Typography>
    </Alert>
  );
}
